import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { TableRow } from '@mui/material'
import { DefaultHeaderCell } from 'shared/ui/TableNew/ui/DefaultHeaderCell/DefaultHeaderCell'
import tableStyles from 'shared/ui/TableNew/ui/Table.module.scss'
import { TableCell } from 'shared/ui/TableNew/ui/TableCell/TableCell'
import { PinnedCell } from 'shared/ui/TableNew/ui/PinnedCell/PinnedCell'
import type { IColumn, ITableProps, IRowBase, TableStore } from 'shared/ui/TableNew'
import { CheckboxCell } from 'shared/ui/TableNew/ui/CheckboxCell/CheckboxCell'
import { HeaderRightContent } from 'shared/ui/TableNew/ui/TableRowHead/HeaderRightContent/HeaderRightContent'
import styles from './styles.module.scss'

export type ITableRowHeadProps<TRow extends IRowBase> = {
  store: TableStore<TRow>
  selected: boolean
  withCheckbox: ITableProps<TRow>['withCheckbox']
  rightHeaderContent?: ITableProps<TRow>['rightHeaderContent']
  withActions?: boolean
  isScrollLeft: boolean
  onSort?: (field: string) => void
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export const TableRowHead = observer(
  <TRow extends IRowBase>({
    selected,
    withCheckbox,
    rightHeaderContent,
    isScrollLeft,
    sortOrder,
    sortBy,
    onSort,
    withActions,
    store,
  }: ITableRowHeadProps<TRow>) => {
    const { allVisibleRowsSelected, bulkStore, columnsStore } = store
    const { bulkAllMode } = bulkStore
    const { columns, getPinnedColumns, getUnpinnedColumns, visibleColumnsIds } = columnsStore
    const pinnedColumns = getPinnedColumns(columns)
    const unpinnedColumns = getUnpinnedColumns(columns)
    const defaultRenderHeaderCell = <TRow,>(column: IColumn<TRow>) => {
      if (column.renderHeaderCell) {
        return column.renderHeaderCell(column)
      }

      return <DefaultHeaderCell column={column} sortBy={sortBy} sortOrder={sortOrder} />
    }

    const toggleSelected = () => {
      bulkStore.toggleSelectAll()
    }

    const columnCellRender = (column: IColumn<TRow>, index: number) => {
      const isSortable = column.isSortable && onSort
      const onSortAction = () => {
        if (isSortable) {
          onSort(column.sortKey || column.field)
        }
      }

      return (
        <TableCell
          className={classNames({
            [tableStyles.visibilityHidden]: selected,
            [tableStyles.sortable]: isSortable,
          })}
          key={`${column.name}-${index}`}
          width={column.width}
          isHeader={true}
          maxWidth={column.maxWidth}
          minWidth={column.minWidth}
          padding={column.paddingHeader || column.padding}
          onCellClick={isSortable ? onSortAction : undefined}
        >
          {() => defaultRenderHeaderCell(column)}
        </TableCell>
      )
    }

    const isRightContent = visibleColumnsIds || rightHeaderContent || withActions

    return (
      <>
        <TableRow className={classNames(tableStyles.pinnedHeader, tableStyles.tableRowHead)}>
          {(!!pinnedColumns.length || withCheckbox) && (
            <PinnedCell
              isHeader
              isScrollLeft={isScrollLeft}
              withEmptyCell={pinnedColumns.length === 1 && !withCheckbox}
            >
              {withCheckbox && (
                <CheckboxCell
                  selected={selected}
                  isIndeterminate={selected && !bulkAllMode && !allVisibleRowsSelected}
                  onChangeValue={toggleSelected}
                  ariaLabel='SelectAll'
                />
              )}

              {pinnedColumns.map(columnCellRender)}
            </PinnedCell>
          )}
          {unpinnedColumns.map(columnCellRender)}

          {isRightContent && (
            <th className={styles.stickyRightContent}>
              <HeaderRightContent store={store} rightHeaderContent={rightHeaderContent} />
            </th>
          )}
        </TableRow>
      </>
    )
  }
)
